import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { ErrorResponse, Headers, HttpRequestOptions } from '../../../interfaces/http.interface';
import { STORAGE_AUTH_TOKEN } from '../../constants/craig.constants';
import { UtilsService } from '../utils/utils.service';
import { LocalStorageService } from '../local-storage/local-storage.service';

@Injectable({
    providedIn: 'root'
})
export class HttpWrapperService {
    readonly defaultHeaders: Headers = {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache'
    };
    readonly defaultHttpOptions: HttpRequestOptions = {
        observe: 'response',
        headers: new HttpHeaders(this.defaultHeaders)
    };

    constructor(
        private readonly http: HttpClient,
        private readonly utilsService: UtilsService,
        private readonly localStorageService: LocalStorageService
    ) {}

    static getHeaders(httpHeaders: HttpHeaders): Headers {
        const headers: Headers = {};

        httpHeaders.keys().forEach((headerKey: string) => {
            const headerValue: string | null = httpHeaders.get(headerKey);

            if (headerValue !== null) {
                headers[headerKey] = headerValue;
            }
        });

        return headers;
    }

    get<T>(url: string, options: HttpRequestOptions = {}): Observable<HttpResponse<T>> {
        this.setupAuthorizationToken();

        return this.http.get<HttpResponse<T>>(url, this.prepareRequestOptions(options) as any).pipe(
            map((response: any) => this.prepareResponse<T>(response)),
            catchError((err: HttpErrorResponse) => this.errorHandler(err))
        );
    }

    post<T>(url: string, body: any, options: HttpRequestOptions = {}): Observable<HttpResponse<T>> {
        this.setupAuthorizationToken();

        return this.http.post<HttpResponse<T>>(url, body, this.prepareRequestOptions(options) as any).pipe(
            map((response: any) => this.prepareResponse<T>(response)),
            catchError((err: HttpErrorResponse) => this.errorHandler(err))
        );
    }

    put<T>(url: string, body: any, options: HttpRequestOptions = {}): Observable<HttpResponse<T>> {
        this.setupAuthorizationToken();

        return this.http.put<HttpResponse<T>>(url, body, this.prepareRequestOptions(options) as any).pipe(
            map((response: any) => this.prepareResponse<T>(response)),
            catchError((err: HttpErrorResponse) => this.errorHandler(err))
        );
    }

    delete<T>(url: string, options: HttpRequestOptions = {}): Observable<HttpResponse<T>> {
        this.setupAuthorizationToken();

        return this.http.delete<HttpResponse<T>>(url, this.prepareRequestOptions(options) as any).pipe(
            map((response: any) => this.prepareResponse<T>(response)),
            catchError((err: HttpErrorResponse) => this.errorHandler(err))
        );
    }

    private prepareRequestOptions(options: HttpRequestOptions): HttpRequestOptions {
        const clonedOptions: HttpRequestOptions = { ...this.defaultHttpOptions, ...options };

        if (options && options.headers) {
            clonedOptions.headers = {
                ...HttpWrapperService.getHeaders(this.defaultHttpOptions.headers as HttpHeaders),
                ...HttpWrapperService.getHeaders(options.headers as HttpHeaders)
            };
        }

        return clonedOptions;
    }

    private prepareResponse<T>(response: HttpResponse<T>): HttpResponse<T> {
        return response;
    }

    private errorHandler(err: HttpErrorResponse): Observable<never> {
        const error: ErrorResponse | string = this.getParsedError(err.error);
        let errorMsg: string = err.message;

        if (!!error && typeof error !== 'string' && !!error.detail) {
            if (error.detail.error_message) {
                errorMsg = error.detail.error_message;
            }

            if (error.detail.navigate_url) {
                this.navigateToErrorUri(error.detail.navigate_url).subscribe();
            }
        } else if (err.status === HttpStatusCode.BadGateway || err.status === 0) {
            errorMsg =
                'We encountered a problem getting the chat response. If this persists, please refresh the page or restart the application.';
        }

        return throwError(() => new Error(errorMsg));
    }

    private setupAuthorizationToken(): void {
        const authToken: string | null = this.localStorageService.getRawItem(STORAGE_AUTH_TOKEN);

        this.defaultHttpOptions.headers = new HttpHeaders(
            authToken
                ? {
                      ...this.defaultHeaders,
                      Authorization: authToken
                  }
                : {
                      ...this.defaultHeaders
                  }
        );

        this.defaultHttpOptions.withCredentials = true;
    }

    private navigateToErrorUri(navigateUrl: string): Observable<undefined> {
        // form=1006 means that we will redirect the user to a list of allowed applications.
        // In that case, we don't have to clean local storage
        if (navigateUrl.search('form=1006') === -1) {
            this.utilsService.cleanAuthDataStorage();
        }

        return this.utilsService.redirectToUrl(navigateUrl);
    }

    private getParsedError(error: string): ErrorResponse | string {
        try {
            return JSON.parse(error);
        } catch {
            return error;
        }
    }
}
